/* Packing-photo reminder: packed partner parcels still missing Inside / Outside photos. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Camera } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PackingPhotos } from "@/components/QueryFiles";

const MISS = { none: ["No photos yet", "bg-rose-600 text-white"], both: ["Inside + Outside missing", "bg-rose-100 text-rose-800"],
  inside: ["Inside missing", "bg-amber-100 text-amber-800"], outside: ["Outside missing", "bg-amber-100 text-amber-800"] };

const fetchReminders = () => axios.get(`${API}/order-register/queries/photo-reminders`, { headers: getAuthHeader() }).then((r) => r.data);

const ReminderRow = ({ x, i, onDone }) => {
  const [l, cls] = MISS[x.missing];
  return (
    <div className="rounded border p-2" data-testid={`photo-reminder-row-${i}`}>
      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs">
        <b>{x.customer_name}</b><span className="text-slate-500">{x.code} · {x.date} · {x.mode}{x.tracking ? ` · ${x.tracking}` : ""}</span>
        <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`} data-testid={`photo-reminder-missing-${i}`}>{l}</span>
      </div>
      <PackingPhotos rowId={x.row_id} onDone={onDone} />
    </div>
  );
};

export const PhotoReminderDialog = ({ open, onClose, data, reload }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="photo-reminder-dialog">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><Camera className="h-5 w-5 text-rose-600" /> Packing photos not sent yet</DialogTitle></DialogHeader>
      <p className="text-xs text-slate-500">Partner parcels packed in the last 14 days. Take the Inside and Outside photo — the partner sees them in their Hub.</p>
      {data && <div className="flex gap-2 text-[11px]" data-testid="photo-reminder-summary">
        <span className="rounded bg-rose-600 px-2 py-0.5 font-semibold text-white">{data.none} with no photos</span>
        <span className="rounded bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">{data.partial} with one side missing</span></div>}
      <div className="space-y-2">
        {data?.items.map((x, i) => <ReminderRow key={x.row_id} x={x} i={i} onDone={reload} />)}
        {data && !data.items.length && <p className="p-6 text-center text-sm text-emerald-700" data-testid="photo-reminder-empty">All packed parcels have their photos.</p>}
      </div>
    </DialogContent>
  </Dialog>
);

const useReminders = (poll) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => fetchReminders().then(setD).catch(() => {}), []);
  useEffect(() => {
    load();
    window.addEventListener("photo-reminders-reload", load);
    const t = poll ? setInterval(load, 60000) : null;
    return () => { window.removeEventListener("photo-reminders-reload", load); if (t) clearInterval(t); };
  }, [load, poll]);
  return [d, load];
};

export const PhotoReminderBadge = () => {
  const [d, load] = useReminders(true);
  const [open, setOpen] = useState(false);
  if (!d?.count && !open) return null;
  return (<>
    {d?.count > 0 && <button type="button" onClick={() => { load(); setOpen(true); }} title="Packed partner parcels with no packing photos yet"
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-rose-400 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700"
      data-testid="photo-reminder-badge">
      <Camera className="h-3.5 w-3.5" /> Photos missing <span className="rounded-full bg-rose-600 px-1.5 text-white" data-testid="photo-reminder-count">{d.count}</span>
    </button>}
    <PhotoReminderDialog open={open} onClose={() => setOpen(false)} data={d} reload={load} />
  </>);
};

export const PhotoReminderPopup = () => {
  const [d, load] = useReminders(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let uid = "";
    try { uid = JSON.parse(localStorage.getItem("user") || "{}").id || ""; } catch { /* no user */ }
    const k = `photo_reminder_shown_${uid}`;
    if (d?.count && !sessionStorage.getItem(k)) { sessionStorage.setItem(k, "1"); setOpen(true); }
  }, [d]);
  return <PhotoReminderDialog open={open} onClose={() => setOpen(false)} data={d} reload={load} />;
};

const SHORT = { none: "No photos", both: "In+Out", inside: "Inside", outside: "Outside" };

export const PhotoMissingTag = ({ r, onDone }) => {
  const [open, setOpen] = useState(false);
  if (!r.photo_missing) return null;
  const [l, cls] = MISS[r.photo_missing];
  return (<>
    <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} title={`Packing photos: ${l} — click to add`}
      className={`ml-1 inline-flex items-center gap-0.5 rounded px-1 py-0.5 align-middle text-[9px] font-bold ${cls}`} data-testid={`or-photo-missing-${r.id}`}>
      <Camera className="h-2.5 w-2.5" />{SHORT[r.photo_missing]}</button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid={`or-photo-missing-dialog-${r.id}`}>
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><Camera className="h-4 w-4 text-rose-600" /> {r.customer_name} — {l}</DialogTitle></DialogHeader>
        <PackingPhotos rowId={r.id} onDone={() => { setOpen(false); window.dispatchEvent(new Event("photo-reminders-reload")); onDone?.(); }} />
      </DialogContent>
    </Dialog>
  </>);
};
